<script setup lang="ts">
import { capitalize, kebabCase } from 'lodash-es'
import { NodeTypes } from '@marvr/storyblok-rich-text-types'
import { useStructuredData } from 'core/services/ldjson'
import {
  fbShare, twShare,
  useArticleProduct,
  useArticleProductFallback,
  useArticleProductFamily,
  useArticleProductFamilyFallback, useBlogCategory,
} from '../../services/blog'
import SvgFacebookLogo from '../../assets/icons/facebookLogo.svg?url'
import SvgTwitterLogo from '../../assets/icons/twitterLogo.svg?url'

const route = useRoute()
const config = useRuntimeConfig()
const siteName = config.public.siteName
const origin = config.public.host
const platform = config.public.platform
const appConfig = useAppConfig()
const blog = appConfig.blog
const pathName = route.fullPath
const slug = useRemoveTrailingSlash((route.params.article as string[]).join('/'))
const appId = config.public.facebookAppId

const blogApi = useStoryblokBlog()
const article = await blogApi.getArticle(slug)
const commonData = (await blogApi.getHome()).homePage.content

const ogImage = article.content.image.filename.replace('a.storyblok.com', 'img2.storyblok.com/1200x627/filters:format(jpg)')
const articleAuthor = article.content.authors[0]
const canonical = new URL(route.path, config.public.host)
useSchemaOrg([
  {
    '@type': 'WebPage',
  },
  defineArticle(
    {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      'mainEntityOfPage': {
        '@id': 'https://google.com/article',
      },
      'headline': article.content.title,
      'image': {
        '@id': ogImage || '',
      },
      'datePublished': article.content.publishDate,
      'author': {
        '@type': 'Person',
        'name': articleAuthor?.name || '',
        'url': `${origin}/author/${articleAuthor?.slug}`,
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Srovnejto.cz',
        'logo': {
          '@type': 'ImageObject',
          'url': `${origin}/logo.svg`,
        },
      },
    },
  ),
])
if (article.content.name) {
  const { structuredData } = useStructuredData(article.content.name,
    article.content.imageUrl,
    article.content.startDate,
    article.content.endDate,
    article.content.type,
  )
  useHead({
    script: [{ children: structuredData, type: 'application/ld+json' }],
  })
}
useHead({
  title: `${article.content.title}  | ${siteName}`,
  meta: [
    { name: 'description', content: article.content.metaDescription },
    { property: 'og:title', content: article.content.title },
    { property: 'og:type', content: 'article' },
    { property: 'og:image:', content: ogImage },
    { property: 'og:url', content: origin + pathName },
    { property: 'og:site_name', content: 'Srovnejto.cz' },
    { property: 'article:publisher ', content: 'https://www.facebook.com/srovnejto.cz/' },
    { property: 'article:section', content: article.content.categories[0] },
    { property: 'article:published_time', content: article.content.publishDate },
  ],
  link: [{
    rel: 'canonical',
    href: canonical.origin + canonical.pathname,
  }],
})
onMounted(() => {
  window.dataLayer?.push({
    event: 'page_view',
    country_custom: appConfig.locale.code,
    platform,
    page_env: import.meta.env.PROD ? 'prod' : 'dev',
    login_status: 'logged-out',
    page_content_type: 'article',
    p_product_family: article.content.sections?.length
      ? useArticleProductFamily(article.content.sections[0].slug, commonData.categories)
      : useArticleProductFamilyFallback(article.content.categories[0], commonData.categories),
    p_product: article.content.sections?.length
      ? useArticleProduct(article.content.sections[0].slug, commonData.categories)
      : useArticleProductFallback(article.content.categories[0], commonData.categories),
    page_name: `${platform}/owned/article/${slug}`,
    cg1: 'level-3',
    cg2: 'articles',
    cg3: '',
    cg4: article.content.publishDate.substring(0, 10),
    cg5: 'content',
    uid: '',
  })
  window.scroll(0, 0)
})
const breadcrumbs = useState('breadcrumbs')
breadcrumbs.value = { breadcrumbs: [{ slug: `${blog.url}/`, title: capitalize(blog.url.substring(1)) }], title: article.name }
useScriptTag(
  `https://connect.facebook.net/cs_CZ/sdk.js#xfbml=1&version=v13.0&appId=${appId}&autoLogAppEvents=1`,
  () => {
  },
  { async: true, defer: true, crossOrigin: 'anonymous' },
)
useScriptTag(
  'https://platform.twitter.com/widgets.js',
  () => {
  },
  { async: true },
)
// Sticky menu.
const refContent = ref()
const scrollmenu = ref()
const { height } = useElementSize(refContent)
const { height: menuH } = useElementSize(scrollmenu)
const { y: wy } = useWindowScroll()
const navigation = article.content.text.content
  .filter((x: any) => x.type === 'heading' && (x.attrs.level === 2 || x.attrs.level === 3) && x.content?.length)
  .map((x: any) => ({
    title: x.content[0].text,
    target: `#${kebabCase(x.content[0].text)}`,
  }))
const swiperTags = article.content.tags.map((tag: StoryBlokTagItem) => tag.uuid)
const swiperHeading: StoryBlokHeading = [{
  text: blog.swiperName,
  level: 2,
  type: NodeTypes.HEADING,
}]
</script>

<template>
  <article v-if="article" class="bg-white">
    <BlogWrapper :blog-homepage-items="commonData">
      <div class="relative">
        <div class="relative hidden sm:block">
          <StoryblokImage class="rounded-xl shadow-hover w-full z-1" :src="article.content.image" height="350" width="1160" mwidth="800" />
          <div class="absolute top-0 w-full h-full rounded-xl bg-gradient-to-b from-transparent to-black opacity-40 z-1" />
        </div>
        <div class="sm:hidden -m-5 mb-2.5 relative">
          <StoryblokImage class="shadow-hover w-full z-1" :src="article.content.image" mheight="320" mwidth="640" />
          <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-black opacity-40 z-1" />
        </div>
        <div class="block my-0 absolute left-0 top-2.5 sm:left-5 md:top-5 z-1">
          <div class="flex flex-wrap gap-2 lg:gap-2.55">
            <span v-for="(cat, i) in article.content.sections">
              <StoryblokLink
                v-if="useBlogCategory(cat, commonData.categories)"
                :key="i"
                :to="`${blog.url}${useBlogCategory(cat, commonData.categories)?.slug}`"
                class="bg-white rounded-md border border-gray text-sm px-2.5 leading-24px"
              >
                {{ useBlogCategory(cat, commonData.categories)?.name }}
              </StoryblokLink>
            </span>
          </div>
        </div>
        <div class="bottom-5 sm:left-5 text-white absolute z-2">
          <h1 class="py-2.5  headline-4 md:headline-3 lg:py-5 lg:headline-2 lg:text-shadow lg:pr-5 lg:pb-2.5">
            {{ article.content.title }}
          </h1>
          <div class="flex flex-wrap opacity-75 leading-16px">
            <div class="pr-2.5 leading-16px">
              {{ useFormatDate(article.content.publishDate, 'd. M. yyyy') }}
              <span v-if="article.content?.changeDate">
                {{ `| ${blog.updated}: ${useFormatDate(
                  article.content?.changeDate,
                  'd. M. yyyy',
                )}` }}
              </span>
            </div>
            <div>|</div>
            <div class="px-2.5">
              {{ blog.articleLength }}: {{ article.content.readTime }} min.
            </div>
            <div v-if="article.content.authors.length > 0" class="hidden sm:block">
              |
            </div>
            <div v-if="article.content.authors.length > 0" class="pt-1.5 w-full hidden sm:block sm:w-auto sm:pt-0 sm:px-2.5">
              <StoryblokLink v-for="(author, i) in article.content.authors" :to="`${blog.url}/autor/${author.slug}`" class="hover:underline">
                {{ author.name }}<span v-if="i + 1 < article.content.authors.length">,</span>
              </StoryblokLink>
            </div>
            <div v-if="article.content.tags.length > 0" class="hidden lg:block">
              |
            </div>
            <div class="hidden sm:flex-auto sm:block pt-1.5 w-full lg:w-auto lg:pt-0 lg:px-2.5">
              <StoryblokLink v-for="(tag, i) in article.content.tags" :key="i" :to="`${blog.url}/tag/${tag.slug}`" class="pr-2.5">
                #{{ tag.name }}
              </StoryblokLink>
            </div>
          </div>
        </div>
      </div>
      <div class="grid gap-10 pt-5 lg:grid-cols-[360px,auto] relative">
        <div class="w-full hidden lg:block relative">
          <div ref="scrollmenu" :class="{ 'fixed top-5 max-w-360px': wy > 680 && wy + menuH < height + 680 }">
            <h3>{{ blog.articleContent }}</h3>
            <div class="mt-2">
              <a v-for="(item, i) in navigation" :key="i" :href="item.target" class="block p-2.5 mb-2.5 rounded-xl hover:bg-background-secondary">
                {{ item.title }}
              </a>
            </div>
          </div>
        </div>
        <div ref="refContent">
          <div class="bg-white rounded-lg shadow-hover p-5 max-w-760px">
            <StoryblokRichText id="commonData" :document="article.content.text" class="main-text" />
            <div v-if="article.content.authors" class="mt-10 py-2 border-t-1 border-dark-gray flex justify-between">
              <div
                v-for="(author, i) in article?.content.authors" :key="i"
                class="flex items-center pb-2"
              >
                <div v-if="author.content.image?.filename" class="mr-2.5">
                  <StoryblokImage :src="author.content.image" width="40" height="40" class="rounded-lg" />
                </div>
                <div class="flex flex-col">
                  <strong>{{ author.name }}</strong>
                  <StoryblokLink :to="`${blog.url}/autor/${author.slug}`" class="text-primary flex gap-2 items-center">
                    {{ blog.moreFromAutor }}
                    <AppIcon size="14" icon="fas-arrow-right" />
                  </StoryblokLink>
                </div>
              </div>
              <div>
                <h4 class="pb-2">
                  {{ blog.share }}
                </h4>
                <div class="flex gap-2.5">
                  <div class="cursor-pointer" @click="fbShare">
                    <img :src="SvgFacebookLogo" class="text-xl flex justify-center">
                  </div>
                  <div class="cursor-pointer" @click="twShare">
                    <img :src="SvgTwitterLogo" class="text-xl flex justify-center">
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="text-center pt-20">
            <BaseButton render="secondary" :to="blog.url" icon-start="far-angle-left">
              {{ blog.seeAll }}
            </BaseButton>
          </div>
        </div>
      </div>
      <ComponentSwiperBlog :blog-uuid="article.uuid" :title="swiperHeading" :tags="swiperTags" :show-all-url="blog.url" :show-all="blog.swiperSeeAll" class="pt-70px md:pt-140px" />
    </BlogWrapper>
    <StoryblokProductTiles
      v-editable="commonData.tiles[0].reference.content"
      v-bind="commonData.tiles[0].reference.content"
      class="py-70px md:py-140px content-container"
    />
    <ComponentFloatingBar v-if="article.content.FloatingBar?.length" v-bind="article.content.FloatingBar[0]" />
  </article>
</template>

<style lang="scss" scoped>
.main-text {
  :deep(p + p) {
    @apply pt-5
  }
}
@screen sm {
  :deep(table) {
    width: calc(100vw - 90px);
    max-width: fit-content;
  }
}
@screen lg {
  :deep(table) {
    width: calc(100vw - 535px);
  }
}
</style>
